import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import React, { useContext, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { serviceCodeDropdownOptions } from '@/components/PersonInsurancePolicy/NewVOB/serviceCodeOptions';
import AppContext from '@/contexts/appContext';
import { insurancePolicySummariesQueryKey } from '@/hooks/useInsurancePolicySummaries';
import useInsuranceVerificationSources from '@/hooks/useInsuranceVerificationSources';
import useInsuranceVerificationStatuses from '@/hooks/useInsuranceVerificationStatuses';
import PersonContext from '@/pages/Person/PersonContext';
import InsuranceRepository from '@/repositories/InsuranceRepository';
import InsuranceVerificationRepository from '@/repositories/InsuranceVerificationRepository';
import type { InsurancePolicy } from '@/types/insurancePolicy';
import { InsuranceVerification, InsuranceVerificationStatus } from '@/types/insuranceVerification';
import { Button, Datepicker, Input, Select } from '@/ui';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import { currencyDollarsToCentsFormatter } from '@/utils/currencies';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import './styles.css';
import type { Props } from './types';

interface ManualVerificationFormValues {
  status: string;
  verified_via_id: string | null;
  service_code: string;
  verified_through: string;
  estimated_monthly_cost: number;
  estimated_initial_month_cost: number | null;
  deductible_remaining: number | null;
  out_of_pocket_maximum_remaining: number | null;
  annual_deductible: number | null;
  out_of_pocket_maximum: number | null;
  co_pay: number | null;
  co_pay_unit: string | null;
  co_insurance: number | null;
  vob_notes: string;
}

const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const endOfMonth = (monthsAhead: number, from = new Date()) =>
  isoDate(new Date(from.getFullYear(), from.getMonth() + monthsAhead + 1, 0));

const dollars = (cents: number | null | undefined) => (cents == null ? null : cents / 100);

const policyBenefitValues = (policy: InsurancePolicy) => ({
  annual_deductible: policy.annual_deductible ?? null,
  out_of_pocket_maximum: dollars(policy.out_of_pocket_maximum),
  co_pay: dollars(policy.co_pay),
  co_pay_unit: policy.co_pay_unit ?? null,
  co_insurance: policy.co_insurance != null ? Math.round(policy.co_insurance * 100) : null
});

const buildDefaultValues = (policy: InsurancePolicy): ManualVerificationFormValues => ({
  status: '',
  verified_via_id: null,
  service_code: policy.last_verification?.service_code ?? '30',
  verified_through: endOfMonth(0),
  estimated_monthly_cost: null as unknown as number,
  estimated_initial_month_cost: null,
  deductible_remaining: null,
  out_of_pocket_maximum_remaining: null,
  ...policyBenefitValues(policy),
  vob_notes: ''
});

const changedPlanBenefits = (
  values: ManualVerificationFormValues,
  policy: InsurancePolicy
): Partial<InsurancePolicy> => {
  const current = policyBenefitValues(policy);
  const changes: Partial<InsurancePolicy> = {};

  const annualDeductible =
    values.annual_deductible != null ? Math.round(values.annual_deductible) : null;
  if (annualDeductible !== current.annual_deductible) {
    changes.annual_deductible = annualDeductible;
  }
  if (values.out_of_pocket_maximum !== current.out_of_pocket_maximum) {
    changes.out_of_pocket_maximum = currencyDollarsToCentsFormatter(values.out_of_pocket_maximum);
  }
  if (values.co_pay !== current.co_pay) {
    changes.co_pay = currencyDollarsToCentsFormatter(values.co_pay);
  }
  if ((values.co_pay_unit || null) !== current.co_pay_unit) {
    changes.co_pay_unit = values.co_pay_unit;
  }
  if (values.co_insurance !== current.co_insurance) {
    changes.co_insurance = values.co_insurance != null ? values.co_insurance / 100 : null;
  }

  return changes;
};

const toNumber = (raw: string): number | null => (raw === '' ? null : Number(raw));

const ManualVerificationForm = ({ policy, onSaved, onCancel }: Props) => {
  const insuranceRepository = InsuranceRepository();
  const insuranceVerificationRepository = InsuranceVerificationRepository();
  const queryClient = useQueryClient();
  const { addMessage } = useContext(AppContext);
  const {
    person: { id: personId },
    refetch: refetchPerson
  } = useContext(PersonContext);

  const lastVerification = policy.last_verification;
  const [isSaving, setIsSaving] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch
  } = useForm<ManualVerificationFormValues>({
    defaultValues: buildDefaultValues(policy),
    mode: 'onChange'
  });

  const { data: statuses } = useInsuranceVerificationStatuses();
  const { data: sources } = useInsuranceVerificationSources();

  const statusOptions = useMemo(
    () => (statuses ?? []).map((status) => ({ label: status, value: status })),
    [statuses]
  );
  const sourceOptions = useMemo(
    () => (sources ?? []).map(({ id, name }) => ({ label: name, value: id })),
    [sources]
  );

  const arePlanBenefitsLocked = policy.is_mutable === false;
  const verifiedThrough = watch('verified_through');

  const save = async (values: ManualVerificationFormValues) => {
    setIsSaving(true);
    try {
      if (!arePlanBenefitsLocked && policy.id) {
        const benefitChanges = changedPlanBenefits(values, policy);
        if (Object.keys(benefitChanges).length) {
          await insuranceRepository.updateInsurancePolicy(policy.id, benefitChanges);
        }
      }

      const verificationPayload: Partial<InsuranceVerification> & { verified_via_id?: string } = {
        insurance_policy_id: policy.id,
        status: values.status as InsuranceVerificationStatus,
        ...(values.verified_via_id ? { verified_via_id: values.verified_via_id } : {}),
        service_code: values.service_code,
        verified_through: values.verified_through,
        estimated_monthly_cost_in_cents: currencyDollarsToCentsFormatter(
          values.estimated_monthly_cost
        ),
        estimated_initial_month_cost_in_cents: currencyDollarsToCentsFormatter(
          values.estimated_initial_month_cost
        ),
        deductible_remaining: currencyDollarsToCentsFormatter(values.deductible_remaining),
        out_of_pocket_maximum_remaining_in_cents: currencyDollarsToCentsFormatter(
          values.out_of_pocket_maximum_remaining
        ),
        vob_notes: values.vob_notes || null
      };

      await insuranceVerificationRepository.createInsuranceVerification(verificationPayload);

      queryClient.invalidateQueries({ queryKey: insurancePolicySummariesQueryKey(personId) });
      await refetchPerson();
      addMessage('success', 'Verification saved.');
      onSaved();
    } catch (error) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    } finally {
      setIsSaving(false);
    }
  };

  const moneyField = (
    name:
      | 'estimated_monthly_cost'
      | 'estimated_initial_month_cost'
      | 'deductible_remaining'
      | 'out_of_pocket_maximum_remaining',
    label: string,
    rules?: { required: string }
  ) => (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value } }) => (
        <Input
          id={name}
          label={label}
          type="number"
          step="0.01"
          min="0"
          leadingIcon={<span className="text-uniform-gray-500">$</span>}
          value={value ?? ''}
          error={errors[name]?.message}
          onChange={(event) => onChange(toNumber(event.target.value))}
        />
      )}
    />
  );

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit(save)}>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-2 text-sm font-semibold">Result</legend>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="status"
            control={control}
            rules={{ required: 'Status is required' }}
            render={({ field: { onChange, value } }) => (
              <Select
                label="Status *"
                options={statusOptions}
                selectedOption={statusOptions.find((option) => option.value === value) ?? null}
                onChange={(option) => onChange(option?.value ?? '')}
                placeholder="Select a status"
                error={errors.status?.message}
              />
            )}
          />
          <Controller
            name="verified_via_id"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                label="Verified via"
                options={sourceOptions}
                selectedOption={sourceOptions.find((option) => option.value === value) ?? null}
                onChange={(option) => onChange(option?.value ?? null)}
                placeholder="Select a source"
              />
            )}
          />
          <div className="flex flex-col gap-1">
            <Controller
              name="service_code"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  label="Service code"
                  options={serviceCodeDropdownOptions}
                  selectedOption={
                    serviceCodeDropdownOptions.find((option) => option.value === value) ?? null
                  }
                  onChange={(option) => option && onChange(option.value)}
                  placeholder="Select service code"
                />
              )}
            />
            <span className="text-xs text-uniform-gray-500">
              {lastVerification?.service_code
                ? 'From the last verification'
                : 'Default: no earlier verification'}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Controller
              name="verified_through"
              control={control}
              rules={{ required: 'Verified through is required' }}
              render={({ field: { onChange, value, name } }) => (
                <Datepicker
                  label="Verified through *"
                  name={name}
                  value={value}
                  onChange={onChange}
                  error={errors.verified_through?.message}
                />
              )}
            />
            <div className="flex gap-1.5">
              {([0, 1] as const).map((monthsAhead) => {
                const date = endOfMonth(monthsAhead);
                const isSelected = verifiedThrough === date;
                return (
                  <button
                    key={monthsAhead}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() =>
                      setValue('verified_through', date, {
                        shouldValidate: true,
                        shouldDirty: true
                      })
                    }
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                      isSelected
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-uniform-gray-300 bg-white text-uniform-gray-600'
                    }`}>
                    {monthsAhead === 0 ? 'End of this month' : 'End of next month'}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-2 text-sm font-semibold">Costs and what&apos;s left</legend>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {moneyField('estimated_monthly_cost', 'Est. monthly cost *', {
            required: 'Estimated monthly cost is required'
          })}
          {moneyField('estimated_initial_month_cost', 'Est. first-month cost')}
          {moneyField('deductible_remaining', 'Deductible remaining')}
          {moneyField('out_of_pocket_maximum_remaining', 'OOP remaining')}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-sm font-semibold">Plan benefits</legend>
        <span className="text-xs text-uniform-gray-500">
          Saved to the policy record; the verification snapshots them when it&apos;s created.
        </span>
        {arePlanBenefitsLocked && (
          <div className="flex items-start gap-2.5 rounded-md border border-uniform-gray-200 bg-uniform-gray-50 px-3 py-2.5 text-sm text-uniform-gray-700">
            <FontAwesomeIcon icon={faLock} className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {`${policy.carrier ?? 'This policy'} is verified, so these stay locked. They're editable on an unlocked policy, or after an insurance admin unlocks it.`}
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="annual_deductible"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                id="annual_deductible"
                label="Annual deductible"
                type="number"
                step="1"
                min="0"
                disabled={arePlanBenefitsLocked}
                leadingIcon={<span className="text-uniform-gray-500">$</span>}
                value={value ?? ''}
                onChange={(event) => onChange(toNumber(event.target.value))}
              />
            )}
          />
          <Controller
            name="out_of_pocket_maximum"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                id="out_of_pocket_maximum"
                label="Out-of-pocket max"
                type="number"
                step="0.01"
                min="0"
                disabled={arePlanBenefitsLocked}
                leadingIcon={<span className="text-uniform-gray-500">$</span>}
                value={value ?? ''}
                onChange={(event) => onChange(toNumber(event.target.value))}
              />
            )}
          />
          <div className="flex gap-2">
            <Controller
              name="co_pay"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  id="co_pay"
                  label="Co-pay"
                  type="number"
                  step="0.01"
                  min="0"
                  disabled={arePlanBenefitsLocked}
                  leadingIcon={<span className="text-uniform-gray-500">$</span>}
                  value={value ?? ''}
                  onChange={(event) => onChange(toNumber(event.target.value))}
                  className="w-32"
                />
              )}
            />
            <Controller
              name="co_pay_unit"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  id="co_pay_unit"
                  label="Co-pay unit"
                  disabled={arePlanBenefitsLocked}
                  value={value ?? ''}
                  onChange={(event) => onChange(event.target.value || null)}
                  className="grow"
                />
              )}
            />
          </div>
          <Controller
            name="co_insurance"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                id="co_insurance"
                label="Co-insurance"
                type="number"
                step="1"
                min="0"
                max="100"
                disabled={arePlanBenefitsLocked}
                appendedText="%"
                value={value ?? ''}
                onChange={(event) => onChange(toNumber(event.target.value))}
              />
            )}
          />
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-sm font-semibold">Notes</legend>
        <Controller
          name="vob_notes"
          control={control}
          render={({ field: { onChange, value } }) => (
            <RichTextEditor onChange={onChange} value={value} />
          )}
        />
        {lastVerification?.vob_notes && (
          <button
            type="button"
            onClick={() =>
              setValue('vob_notes', lastVerification.vob_notes ?? '', { shouldDirty: true })
            }
            className="self-start text-sm font-medium text-blue-700">
            Copy notes from last verification
          </button>
        )}
      </fieldset>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" color="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" color="primary" isLoading={isSaving}>
          Save verification
        </Button>
      </div>
    </form>
  );
};

export default React.memo(ManualVerificationForm);
